<template>
  <slot :primary-media="primaryMedia" :secondary-media="secondaryMedia" />
</template>

<script>
import { getFeatureToggles } from "@/helpers/featureToggles.js";
import { productUsesCampaignFirstImages } from "@/helpers/collection.js";

export default {
  name: "ProductCardHelper",
  props: {
    imageType: {
      type: String,
      default: "default",
    },
    product: {
      type: Object,
      required: true,
    },
    collectionHandle: {
      type: String,
      default: null,
    },
    useCampaignFirstOverride: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      imageSequenceCurated: true,
    };
  },
  computed: {
    requestedImageType() {
      if (this.imageTypeIsVideo) {
        return this.imageType;
      }

      if (this.isJewelleryProduct) {
        return "ecomImages";
      }

      if (
        this.useCampaignFirstOverride &&
        productUsesCampaignFirstImages(this.product, {
          collectionHandle: this.collectionHandle,
        })
      ) {
        return "campaignImages";
      }

      if (!this.imageType || this.imageType === "default") {
        return "ecomImages";
      }

      return this.imageType;
    },
    /**
     * If the requested `imageType` is `campaignImages` but the product
     * doesn't actually have campaign images, fall back to `ecomImages`.
     */
    effectiveImageType() {
      if (!this.requestedImageType || this.imageTypeIsVideo) {
        return this.requestedImageType;
      }

      if (this.requestedImageType === "campaignImages") {
        const hasCampaignImages =
          this.product?.campaignImages?.references?.edges?.length > 0;

        const hasAltCampaignImage =
          this.product?.altCampaignImages?.reference?.image;

        return hasCampaignImages || hasAltCampaignImage
          ? "campaignImages"
          : "ecomImages";
      }

      return this.requestedImageType;
    },
    imageTypeIsVideo() {
      return this.imageType.toLowerCase().includes("video");
    },
    /** True only when ecomImages metafield has references — not fallback flat lay / catalog. */
    hasEcomImageReferences() {
      const edges = this.product?.ecomImages?.references?.edges;
      return Array.isArray(edges) && edges.length > 0;
    },
    isJewelleryProduct() {
      const hasJewelleryTag = this.product?.tags?.some(
        (tag) => tag.toLowerCase().trim() === "jewellery"
      );
      return hasJewelleryTag || this.product?.productType === "Jewellery";
    },
    primaryMedia() {
      if (this.imageTypeIsVideo) {
        return this.getVideo(this.imageType);
      }

      if (
        this.effectiveImageType === "ecomImages" &&
        !this.hasEcomImageReferences &&
        this.useCampaignFirstOverride &&
        !this.isJewelleryProduct
      ) {
        return this.getImage("campaignImages");
      }

      return this.getImage(this.effectiveImageType);
    },
    secondaryMedia() {
      if (this.effectiveImageType === "ecomImages") {
        if (!this.hasEcomImageReferences) {
          return this.getImage("flatlayImages");
        }
        return this.getImage("campaignImages");
      }

      return this.getImage("ecomImages");
    },
  },
  methods: {
    getImage(imageType) {
      if (this.imageSequenceCurated) {
        // Check for feature toggle and replace campaignImages with altCampaignImages
        const { useAltCampaignImages = false } = getFeatureToggles();

        if (useAltCampaignImages && imageType === "campaignImages") {
          // Try altCampaignImages first, then fall back to campaignImages if not available
          const altCampaignReference =
            this.product.altCampaignImages?.reference;

          if (altCampaignReference?.image) {
            return altCampaignReference.image;
          }

          // Fall back to normal campaignImages if altCampaignImages not available
          const campaignReference = this.getFirstReference(
            this.product.campaignImages?.references?.edges
          );
          if (campaignReference) {
            return campaignReference?.image || campaignReference;
          }
        }

        // For all other cases or when altCampaignImages feature is disabled
        const reference =
          this.getFirstReference(this.product[imageType]?.references?.edges) ||
          this.getFirstReference(
            this.product["flatlayImages"]?.references?.edges
          ) ||
          this.getFirstReference(this.product.images?.edges);

        return reference?.image || reference;
      }
      return this.getFirstReference(this.product.images?.edges);
    },
    getVideo(imageType) {
      const video = this.getFirstReference(
        this.product[imageType]?.references?.edges
      );

      if (!video) {
        return this.getFirstReference(this.product.images?.edges);
      }

      return video;
    },
    getFirstReference(references) {
      if (!references) return;
      const nodes = references.map((reference) => reference.node);
      return nodes[0];
    },
  },
};
</script>
